import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { MessageCircle, Trash2, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function CommentsPopover({ tripId, comments, onChange, side = 'left' }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  // Optimistic local list so adding a note doesn't force a parent refetch that
  // would tear down the Radix popover mid-interaction.
  const [local, setLocal] = useState(comments || []);
  const [dirty, setDirty] = useState(false);

  // Sync when parent hands us a fresh list (only when popover is closed to
  // avoid stomping the user's in-progress edits).
  useEffect(() => {
    if (!open) { setLocal(comments || []); setDirty(false); }
  }, [comments, open]);

  const flushIfDirty = (nextOpen) => {
    setOpen(nextOpen);
    if (!nextOpen && dirty) { onChange?.(); setDirty(false); }
  };

  const count = local.length;

  const add = async () => {
    const t = text.trim();
    if (!t) return;
    if (t.length > 500) { toast.error('Max 500 characters'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/driver-trips/${tripId}/comments`, { text: t });
      setLocal((prev) => [...prev, r.data]);
      setDirty(true);
      setText('');
      toast.success('Note added');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const remove = async (commentId) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await axios.delete(`${API}/driver-trips/${tripId}/comments/${commentId}`);
      setLocal((prev) => prev.filter((c) => c.id !== commentId));
      setDirty(true);
      toast.success('Removed');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <Popover open={open} onOpenChange={flushIfDirty}>
      <PopoverTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          className={`h-7 px-2 ${count ? 'border-indigo-400 text-indigo-700 hover:bg-indigo-50' : 'text-slate-500'}`}
          data-testid={`comment-btn-${tripId}`}
          title={count ? `${count} note${count === 1 ? '' : 's'}` : 'Add a note'}
        >
          <MessageCircle className="h-3.5 w-3.5" />
          {count > 0 && <span className="ml-1 font-semibold">{count}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent side={side} align="end" className="w-80 p-3" data-testid={`comment-popover-${tripId}`}>
        <div className="text-sm font-semibold mb-2 flex items-center justify-between">
          <span>Notes ({count})</span>
          <button
            onClick={() => setOpen(false)}
            className="text-slate-400 hover:text-slate-700"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-2 max-h-52 overflow-auto mb-2">
          {count === 0 ? (
            <div className="text-xs text-muted-foreground italic">No notes yet.</div>
          ) : (
            local.map((c) => (
              <div key={c.id} className="rounded border border-slate-200 bg-slate-50 p-2 text-xs" data-testid={`comment-${c.id}`}>
                <div className="text-slate-800">{c.text}</div>
                <div className="flex items-center justify-between mt-1 text-[10px] text-slate-500">
                  <span>{c.author} · {new Date(c.created_at).toLocaleString('en-ZA')}</span>
                  <button onClick={() => remove(c.id)} className="text-rose-500 hover:text-rose-700" data-testid={`comment-delete-${c.id}`}>
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
        <Textarea
          rows={2}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="e.g. late start, breakdown 09:30, praise for finishing early"
          className="text-sm"
          data-testid={`comment-input-${tripId}`}
        />
        <div className="flex justify-end mt-2">
          <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={add} disabled={busy || !text.trim()} data-testid={`comment-add-${tripId}`}>
            {busy ? 'Saving…' : 'Add note'}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
